import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, ShieldCheck } from "lucide-react";
import { statusLabel, formatApiError } from "../../lib/format";
import api from "../../lib/api";

/**
 * Delete lease modal — soft-delete (Recycle Bin) for unsigned leases,
 * or strict typed-confirmation force-delete for signed leases.
 */
export default function LeaseDeleteModal({ lease, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isUnsigned = ["draft", "sent_to_tenant", "cancelled"].includes(lease.lease_status);

  const confirm = async () => {
    const signed = !isUnsigned;
    if (signed) {
      if (typed !== "DELETE") { toast.error('Type "DELETE" exactly to confirm'); return; }
      if (!reason.trim() || reason.trim().length < 10) { toast.error("Reason must be at least 10 characters"); return; }
    }
    setDeleting(true);
    try {
      const qs = signed ? "?force=true" : "";
      await api.delete(`/leases/${lease.id}${qs}`, { data: { reason } });
      toast.success(signed ? "Signed lease force-deleted — recoverable for 30 days" : "Lease moved to Recycle Bin (30-day restore window)");
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !deleting && onClose()}>
      <div className="card-modav p-6 w-full max-w-lg border-t-4 border-[#C92A2A]" onClick={(e) => e.stopPropagation()} data-testid="delete-modal">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 shrink-0 rounded-full bg-[#FEF0F0] text-[#C92A2A] flex items-center justify-center">
            <Trash2 size={20}/>
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-xl font-bold text-[#C92A2A]">Delete this lease?</h3>
            <p className="text-xs text-[#5E7166] mt-1">Lease <b>{lease.id.slice(0,8)}</b> for <b>{lease.tenant?.full_name || "—"}</b> on <b>{lease.unit?.unit_name || "—"}</b></p>
          </div>
        </div>

        {!isUnsigned ? (
          <div className="bg-[#FEF0F0] border-2 border-[#C92A2A] rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2 mb-2">
              <ShieldCheck size={16} className="text-[#C92A2A] mt-0.5 shrink-0"/>
              <div className="text-sm text-[#C92A2A] font-bold uppercase tracking-wider">⚠ Strict Warning — Signed Lease</div>
            </div>
            <ul className="text-xs text-[#7A2020] space-y-1 list-disc list-inside">
              <li>This lease is in <b>{statusLabel(lease.lease_status)}</b> status and forms part of the legal record.</li>
              <li>Deletion is audit-logged with your name + reason and is reversible from the Recycle Bin for <b>30 days</b>.</li>
              <li>After 30 days the lease is purged permanently. <b>This action cannot be undone after that.</b></li>
              <li>If any payments are linked to this lease, deletion will be refused — financial trail integrity.</li>
            </ul>
          </div>
        ) : (
          <div className="bg-[#FDF6E3]/60 border border-[#EBDDA7] rounded-lg p-3 mb-4 text-xs text-[#A0801C]">
            This unsigned lease will be moved to the Recycle Bin and can be restored within 30 days. No legal record is destroyed.
          </div>
        )}

        <label className="block mb-3">
          <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">
            Reason {!isUnsigned && <span className="text-[#C92A2A]">(required, min 10 chars)</span>}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder={!isUnsigned ? "e.g. Duplicate from data migration — superseded by lease #abc12345" : "Optional explanation for the audit log"}
            className="input-modav mt-1 resize-none"
            data-testid="delete-reason"
          />
        </label>

        {!isUnsigned && (
          <label className="block mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">
              Type <code className="bg-[#FEF0F0] text-[#C92A2A] font-bold px-1">DELETE</code> to confirm
            </span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              className="input-modav mt-1 font-mono"
              data-testid="delete-typed"
            />
          </label>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={deleting} className="btn-ghost flex-1 justify-center" data-testid="delete-cancel-btn">Cancel</button>
          <button
            onClick={confirm}
            disabled={deleting || (!isUnsigned && (typed !== "DELETE" || reason.trim().length < 10))}
            className="flex-1 justify-center inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#C92A2A] text-white hover:bg-[#A52020] disabled:bg-[#5E7166] disabled:cursor-not-allowed text-sm font-bold"
            data-testid="delete-confirm-btn"
          >
            <Trash2 size={14}/> {deleting ? "Deleting…" : isUnsigned ? "Move to Recycle Bin" : "Force-Delete Signed Lease"}
          </button>
        </div>
      </div>
    </div>
  );
}
